/**
 * BillingDriftWidget — Widget dashboard : dérive facturation portefeuille
 * (facturé réel vs contractuel).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, TrendingDown, ArrowRight, Loader2, AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";

const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);

export default function BillingDriftWidget({ partyType = "client", compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/contracts/billing-drift-portfolio?party_type=${partyType}`);
        setData(r.data);
      } catch { setData({ items: [] }); }
    })();
  }, [partyType]);

  if (!data) return (
    <Card className="p-3 text-xs text-slate-500 flex items-center gap-2">
      <Loader2 className="h-3 w-3 animate-spin" /> Analyse dérive facturation…
    </Card>
  );

  if (!data.items?.length || data.contracts_analyzed === 0) return null;

  const drift = data.total_drift_ht || 0;
  const isClient = partyType === "client";
  // Client: negative drift = sous-facturation = mauvais.
  // Supplier: positive drift = surfacturation = mauvais.
  const isBad = isClient ? drift < 0 : drift > 0;
  const TrendIcon = drift >= 0 ? TrendingUp : TrendingDown;

  return (
    <Card className={cn("border-2",
      isBad ? "border-red-300 bg-gradient-to-r from-red-50 to-orange-50/50"
            : "border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50/50")}
          data-testid="billing-drift-widget">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={cn("w-8 h-8 rounded-full flex items-center justify-center",
              isBad ? "bg-red-500" : "bg-emerald-500")}>
              <TrendIcon className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">
                {isClient ? "ARR facturé vs contractuel" : "Facturation fournisseurs"} :
                <span className={cn("ml-2 font-mono",
                  isBad ? "text-red-700" : "text-emerald-700")}>
                  {drift >= 0 ? "+" : ""}{fmtEUR(drift)}
                </span>
              </div>
              <div className="text-[11px] text-slate-600">
                {data.contracts_analyzed} contrat(s) · {data.anomalous_contracts} anomalie(s) ·
                {" "}{data.total_drift_pct}% écart cumulé
              </div>
            </div>
          </div>
        </div>

        <div className={cn("space-y-1.5 overflow-y-auto", compact ? "max-h-40" : "max-h-72")}>
          {data.items.slice(0, compact ? 3 : 10).map((it) => (
            <button key={it.contract_id}
              onClick={() => navigate(`/app/contracts/${it.contract_id}`)}
              className="w-full flex items-center gap-2 bg-white rounded-lg border border-slate-200 hover:border-blue-400 p-2 text-left transition"
              data-testid={`drift-item-${it.contract_id}`}>
              {it.alerts_count > 0 && <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate">{it.title || it.party_name}</div>
                <div className="text-[10px] text-slate-500 truncate">
                  {it.top_alert || `${it.party_name} · attendu ${fmtEUR(it.expected_ht)}`}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={cn("text-xs font-mono font-semibold tabular-nums",
                  Math.abs(it.drift_pct) < 5 ? "text-slate-600"
                  : (it.drift_ht > 0 && !isClient) || (it.drift_ht < 0 && isClient)
                    ? "text-red-700" : "text-emerald-700")}>
                  {it.drift_ht > 0 ? "+" : ""}{fmtEUR(it.drift_ht)}
                </div>
                <div className="text-[9px] text-slate-500 tabular-nums">
                  {it.drift_pct > 0 ? "+" : ""}{it.drift_pct}%
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}
